import { Component, OnInit } from '@angular/core';
import { ExportService } from './export.service';
import { AdminService } from '../admin/admin.service';
import { AuthService } from '../../core/auth/auth.service';
import { IGroup } from '@models/general/group';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TopBarService } from 'src/app/top-bar/top-bar.service';
import { ChangeDetectorRef } from '@angular/core';
import { LocationService } from '../location/location.service';
import { ILocation } from '@models/restore-id/location';
import { ITrap } from '@models/restore-id/trap';
import { MatSelectChange } from '@angular/material/select';
import { TrapService } from '../location/trap/trap.service';
import { HttpResponse } from '@angular/common/http';

interface EXPORTForm {
  groupid: FormControl<string | null>;
  export_type: FormControl<string | null>;
  photos_included: FormControl<string | null>;
  filter_type: FormControl<string | null>;
  location_names: FormControl<string[] | null>;
  trap_names: FormControl<string[] | null>;
  keyword_name: FormControl<string | null>;
  keyword_name_condition: FormControl<string | null>;
  created_by: FormControl<string | null>;
}

@Component({
  selector: 'restoreid-export',
  templateUrl: './export.component.html',
  styleUrls: ['./export.component.scss'],
})
export class ExportComponent implements OnInit {
  exportForm: FormGroup<EXPORTForm>;

  constructor(
    private _exportService: ExportService,
    private _adminService: AdminService,
    private _authService: AuthService,
    private _topBarService: TopBarService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _locationService: LocationService,
    private _trapService: TrapService,
  )
  {
    this._topBarService.setTitle('Export data');

    this.exportForm = new FormGroup<EXPORTForm>({
      groupid: new FormControl<string | null>(null),
      export_type: new FormControl<string | null>(null, Validators.required),
      photos_included: new FormControl<string | null>(null),
      filter_type: new FormControl<string | null>(null),
      location_names: new FormControl<string[] | null>(null),
      trap_names: new FormControl<string[] | null>(null),
      keyword_name: new FormControl<string | null>(null),
      keyword_name_condition: new FormControl<string | null>(null),
      created_by: new FormControl<string | null>(null)
    });
  }

  exportOptions = [
    { value: 'All data' },
    { value: 'Filtered data' },
  ];

  filterOptions = [
    { value: 'Full name' },
    { value: 'Partial name' },
  ];

  conditionOptions = [
    { value: 'Start' },
    { value: 'Included' },
    { value: 'Equal to' },
  ];

  selectedGroup: string = '';

  public potentialGroups : IGroup[] = [];
  public potentialLocations : ILocation[] = [];
  public potentialTraps : ITrap[] = [];

  async ngOnInit() {
    await this._getPotentialGroups();
    this._setValidators();
  }

  private _setValidators() {
    this.exportForm.controls.export_type.valueChanges.subscribe(value => {
      if(value === 'All data'){
        this.exportForm.controls.filter_type.clearValidators();
        this.exportForm.controls.location_names.clearValidators();
        this.exportForm.controls.trap_names.clearValidators();
        this.exportForm.controls.keyword_name.clearValidators();
        this.exportForm.controls.keyword_name_condition.clearValidators();

        this.exportForm.controls.filter_type.setValue(null);
        this.exportForm.controls.location_names.setValue(null);
        this.exportForm.controls.trap_names.setValue(null);
        this.exportForm.controls.keyword_name.setValue(null);
        this.exportForm.controls.keyword_name_condition.setValue(null);
      }
      else if(value === 'Filtered data'){
        this.exportForm.controls.filter_type.addValidators(Validators.required);
      }
      this._changeDetectorRef.detectChanges();
    });

    this.exportForm.controls.filter_type.valueChanges.subscribe(value => {
      if(value === 'Full name'){
        this.exportForm.controls.keyword_name.clearValidators();
        this.exportForm.controls.keyword_name_condition.clearValidators();
        this.exportForm.controls.keyword_name.setValue(null);
        this.exportForm.controls.keyword_name_condition.setValue(null);

        this.exportForm.controls.location_names.addValidators(Validators.required);
      }
      else if(value === 'Partial name'){
        this.exportForm.controls.location_names.clearValidators();
        this.exportForm.controls.location_names.setValue(null);
        this.exportForm.controls.trap_names.setValue(null);

        this.exportForm.controls.keyword_name.addValidators(Validators.required);
        this.exportForm.controls.keyword_name_condition.addValidators(Validators.required);
      }
      this._changeDetectorRef.detectChanges();
    });
  }

  async selectGroup(groupId: string){
    this.selectedGroup = groupId;
    await this._getPotentialLocations();
  }

  async locationChangeSelected(event: MatSelectChange) {
    await this._getPotentialTraps(event.value);
  }

  get exportButtonDisabled() {
    return this.exportForm.invalid;
  }

  private _clearExistingForm() {
    this.exportForm.reset();
  }

  private async _getPotentialGroups(){
    if(this._authService.loggedInUser!.isadmin){
      this.potentialGroups = await this._adminService.getGroups();
      const newGroup: IGroup = {id: '0', name: 'All Groups'};
      this.potentialGroups.unshift(newGroup);
    }
    else{
      this.potentialGroups = await this._adminService.getUserGroups(this._authService.loggedInUserId!);
    }
  }

  private async _getPotentialLocations(){
    this.potentialLocations = await this._locationService.getLocationsByGroupId(this.selectedGroup);
  }

  private async _getPotentialTraps(selectedLocationNames: string[]){
    this.potentialTraps = await this._trapService.getTrapsByMultipleLocations(selectedLocationNames);
  }

  public exportData(): void {
    let rawValues = this.exportForm.getRawValue()!;
    rawValues.created_by = this._authService.loggedInUser!.login;

    if(this.selectedGroup === '0'){
      rawValues.groupid = null;
    }
    else{
      rawValues.groupid = this.selectedGroup;
    }

    if(rawValues.keyword_name_condition){
      rawValues.keyword_name_condition = rawValues.keyword_name_condition.toLowerCase();
    }

    let trapNames = rawValues.trap_names;
    if(trapNames && trapNames.length > 0){
      rawValues.trap_names = trapNames.filter((trapName: string) => trapName !== '');
    }
    if(rawValues.trap_names && rawValues.trap_names.length === 0){
      rawValues.trap_names = null;
    }

    this._exportService.exportData(rawValues).subscribe((blob: Blob) => {
      const contentType = blob.type;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;

      if (contentType === 'application/zip') {
        anchor.download = this._generateFileName() + '.zip';
      }
      else {
        anchor.download = this._generateFileName() + '.xlsx';
      }

      anchor.click();
      window.URL.revokeObjectURL(url);
      this._clearExistingForm();
    });
  }

  private _generateFileName() {
    const date = this._getCurrentDateTime();

    let groupName = '';
    if(this.selectedGroup && this.selectedGroup !== '0'){
      groupName = this.potentialGroups.find(group => group.id === this.selectedGroup)!.name;
    }
    else{
      groupName = 'All_Groups';
    }

    return `RestoreID_Export_${groupName}_${date}`;
  }

  private _getCurrentDateTime(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1); // Months are 0-based
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}_${month}_${day}_${hours}_${minutes}_${seconds}`;
  };

  public get ExportTypeControl() {
    return this.exportForm.get('export_type');
  }

  public get FilterTypeControl() {
    return this.exportForm.get('filter_type');
  }
}
